import { hasAvailableDevices } from '../base/devices/functions.web';
import { MEET_FEATURES } from '../base/jwt/constants';
import { isJwtFeatureEnabled } from '../base/jwt/functions';
import { IGUMPendingState } from '../base/media/types';
import { isMobileScreenshareFocusMode } from '../meeting-ui/functions.any';
import { isScreenMediaShared } from '../screen-share/functions';
import { isWhiteboardVisible } from '../whiteboard/functions';
import { DEFAULT_NARROW_MAIN_TOOLBAR_BUTTONS, DEFAULT_REDUCED_UI_MAIN_TOOLBAR_BUTTONS, MAIN_TOOLBAR_BUTTONS_PRIORITY, MAIN_TOOLBAR_NINE_BUTTONS_WIDTH, TOOLBAR_TIMEOUT } from './constants';
import { isButtonEnabled } from './functions.any';
export * from './functions.any';
/**
 * Helper for getting the height of the toolbox.
 *
 * @returns {number} The height of the toolbox.
 */
export function getToolboxHeight() {
    const toolbox = document.getElementById('new-toolbox');
    return toolbox?.clientHeight || 0;
}
/**
 * Indicates if the toolbox is visible or not.
 *
 * @param {IReduxState} state - The state from the Redux store.
 * @returns {boolean} - True to indicate that the toolbox is visible, false -
 * otherwise.
 */
export function isToolboxVisible(state) {
    const { iAmRecorder, iAmSipGateway, toolbarConfig } = state['features/base/config'];
    const { alwaysVisible } = toolbarConfig || {};
    const { timeoutID, visible } = state['features/toolbox'];
    const { audioSettingsVisible, videoSettingsVisible } = state['features/settings'];
    const whiteboardVisible = isWhiteboardVisible(state);
    // 手机共享聚焦时 alwaysVisible 不强制常显，以便点按隐藏上下栏。
    const respectAlwaysVisible = alwaysVisible && !isMobileScreenshareFocusMode(state);
    return Boolean(!iAmRecorder && !iAmSipGateway
        && (timeoutID
            || visible
            || respectAlwaysVisible
            || audioSettingsVisible
            || videoSettingsVisible
            || whiteboardVisible));
}
/**
 * Indicates if the audio settings button is disabled or not.
 *
 * @param {IReduxState} state - The state from the Redux store.
 * @returns {boolean}
 */
export function isAudioSettingsButtonDisabled(state) {
    return !(hasAvailableDevices(state, 'audioInput')
        || hasAvailableDevices(state, 'audioOutput'))
        || state['features/base/config'].startSilent;
}
/**
 * Indicates if the desktop share button is disabled or not.
 *
 * @param {IReduxState} state - The state from the Redux store.
 * @returns {boolean}
 */
export function isDesktopShareButtonDisabled(state) {
    const { muted, unmuteBlocked } = state['features/base/media'].video;
    const videoOrShareInProgress = !muted || isScreenMediaShared(state);
    const enabledInJwt = isJwtFeatureEnabled(state, MEET_FEATURES.SCREEN_SHARING, true);
    return !enabledInJwt || (unmuteBlocked && !videoOrShareInProgress);
}
/**
 * Indicates if the video settings button is disabled or not.
 *
 * @param {IReduxState} state - The state from the Redux store.
 * @returns {boolean}
 */
export function isVideoSettingsButtonDisabled(state) {
    return !hasAvailableDevices(state, 'videoInput');
}
/**
 * Indicates if the video mute button is disabled or not.
 *
 * @param {IReduxState} state - The state from the Redux store.
 * @returns {boolean}
 */
export function isVideoMuteButtonDisabled(state) {
    const { muted, unmuteBlocked, gumPending } = state['features/base/media'].video;
    return !hasAvailableDevices(state, 'videoInput')
        || (unmuteBlocked && Boolean(muted))
        || gumPending !== IGUMPendingState.NONE;
}
/**
 * If an overflow drawer should be displayed or not.
 * This is usually done for mobile devices or on narrow screens.
 *
 * @param {IReduxState} state - The state from the Redux store.
 * @returns {boolean}
 */
export function showOverflowDrawer(state) {
    return state['features/toolbox'].overflowDrawer;
}
/**
 * Returns the toolbar timeout from config or the default value.
 *
 * @param {IReduxState} state - The state from the Redux store.
 * @returns {number} - Toolbar timeout in milliseconds.
 */
export function getToolbarTimeout(state) {
    const { toolbarConfig } = state['features/base/config'];
    return toolbarConfig?.timeout || TOOLBAR_TIMEOUT;
}
/**
 * Sets the notify click mode for the buttons.
 *
 * @param {Object} buttons - The list of toolbar buttons.
 * @param {Map} buttonsWithNotifyClick - The buttons notify click configuration.
 * @returns {void}
 */
function setButtonsNotifyClickMode(buttons, buttonsWithNotifyClick) {
    if (typeof APP === 'undefined' || (buttonsWithNotifyClick?.size ?? 0) <= 0) {
        return;
    }
    Object.values(buttons).forEach((button) => {
        if (typeof button === 'object') {
            button.notifyMode = buttonsWithNotifyClick.get(button.key);
        }
    });
}
/**
 * Returns all buttons that need to be rendered.
 *
 * @param {IGetVisibleButtonsParams} params - The parameters needed to extract the visible buttons.
 * @returns {Object} - The visible buttons arrays .
 */
export function getVisibleButtons({ allButtons, buttonsWithNotifyClick, toolbarButtons, clientWidth, jwtDisabledButtons, mainToolbarButtonsThresholds }) {
    setButtonsNotifyClickMode(allButtons, buttonsWithNotifyClick);
    const filteredButtons = Object.keys(allButtons).filter(key => typeof key !== 'undefined' // filter invalid buttons that may be coming from config.mainToolbarButtons
        // override
        && !jwtDisabledButtons.includes(key)
        && isButtonEnabled(key, toolbarButtons));
    const { order } = mainToolbarButtonsThresholds.find(({ width }) => clientWidth > width)
        || mainToolbarButtonsThresholds[mainToolbarButtonsThresholds.length - 1];
    const mainToolbarButtonKeysOrder = [
        ...order.filter(key => filteredButtons.includes(key)),
        ...MAIN_TOOLBAR_BUTTONS_PRIORITY.filter(key => !order.includes(key) && filteredButtons.includes(key)),
        ...filteredButtons.filter(key => !order.includes(key) && !MAIN_TOOLBAR_BUTTONS_PRIORITY.includes(key))
    ];
    let mainButtonsKeys = mainToolbarButtonKeysOrder.slice(0, order.length);
    const overflowMenuButtons = filteredButtons.reduce((acc, key) => {
        if (!mainButtonsKeys.includes(key)) {
            acc.push(allButtons[key]);
        }
        return acc;
    }, []);
    // if we have 1 button in the overflow menu it is better to directly display it in the main toolbar by replacing
    // the "More" menu button with it.
    if (overflowMenuButtons.length === 1) {
        const button = overflowMenuButtons.shift()?.key;
        button && mainButtonsKeys.push(button);
    }
    // 防御：宽度不足以容纳「举手 + 快捷输入」时，即使 mainToolbarButtons 误把 raisehand
    // 留在较窄档，也强制收进更多操作，避免左栏与 mic/cam 设备箭头重叠。
    // 须放在「overflow 仅 1 项则抬回主栏」之后，防止举手被再次抬回。
    if (clientWidth <= MAIN_TOOLBAR_NINE_BUTTONS_WIDTH && mainButtonsKeys.includes('raisehand')) {
        mainButtonsKeys = mainButtonsKeys.filter(key => key !== 'raisehand');
        if (allButtons.raisehand && !overflowMenuButtons.some(({ key }) => key === 'raisehand')) {
            overflowMenuButtons.push(allButtons.raisehand);
        }
    }
    const mainMenuButtons = mainButtonsKeys.map(key => allButtons[key]);
    return {
        mainMenuButtons,
        overflowMenuButtons
    };
}
/**
 * Returns buttons that need to be rendered for reduced UI mode.
 *
 * @param {IGetVisibleButtonsForReducedUIParams} params - The parameters needed to extract the visible buttons.
 * @returns {Object} - The visible buttons for reduced ui.
 */
export function getVisibleButtonsForReducedUI({ allButtons, buttonsWithNotifyClick, jwtDisabledButtons, reducedUImainToolbarButtons }) {
    setButtonsNotifyClickMode(allButtons, buttonsWithNotifyClick);
    if (!Array.isArray(reducedUImainToolbarButtons) || reducedUImainToolbarButtons.length === 0) {
        const defaultButtons = DEFAULT_REDUCED_UI_MAIN_TOOLBAR_BUTTONS.map(key => allButtons[key]);
        return {
            mainMenuButtons: defaultButtons
        };
    }
    const filteredButtons = reducedUImainToolbarButtons.filter(key => typeof key !== 'undefined'
        && !jwtDisabledButtons.includes(key)
        && isButtonEnabled(key, reducedUImainToolbarButtons)
        && allButtons[key]);
    const mainMenuButtons = filteredButtons.map(key => allButtons[key]);
    return {
        mainMenuButtons
    };
}
/**
 * 窄屏/手机底栏：主栏固定为 config.narrowMainToolbarButtons（或缺省四钮：麦/摄像头/共享/成员），其余进更多。
 * hangup 由顶栏「结束」承担，不进入 overflow。
 *
 * @param {IGetVisibleButtonsForNarrowLayoutParams} params - 参数。
 * @returns {{ mainMenuButtons: IToolboxButton[]; overflowMenuButtons: IToolboxButton[]; }}
 */
export function getVisibleButtonsForNarrowLayout({ allButtons, buttonsWithNotifyClick, jwtDisabledButtons, narrowMainToolbarButtons, toolbarButtons }) {
    setButtonsNotifyClickMode(allButtons, buttonsWithNotifyClick);
    const configuredMainKeys = Array.isArray(narrowMainToolbarButtons) && narrowMainToolbarButtons.length > 0
        ? narrowMainToolbarButtons
        : DEFAULT_NARROW_MAIN_TOOLBAR_BUTTONS;
    const filteredButtons = Object.keys(allButtons).filter(key => typeof key !== 'undefined'
        && key !== 'hangup'
        && !jwtDisabledButtons.includes(key)
        && isButtonEnabled(key, toolbarButtons));
    const mainButtonsKeys = configuredMainKeys.filter(key => filteredButtons.includes(key) && Boolean(allButtons[key]));
    const overflowMenuButtons = filteredButtons.reduce((acc, key) => {
        if (!mainButtonsKeys.includes(key)) {
            acc.push(allButtons[key]);
        }
        return acc;
    }, []);
    return {
        mainMenuButtons: mainButtonsKeys.map(key => allButtons[key]),
        overflowMenuButtons
    };
}
/**
 * 快捷聊天输入是否应显示：宽屏且当前主栏阈值档仍包含举手（与第一档收起同步）。
 * 另要求宽度超过 9 钮档阈值，防止错误配置把 raisehand 留在窄档时仍显示快捷输入。
 *
 * @param {number} clientWidth - videoSpaceWidth。
 * @param {boolean} isNarrowOrMobile - 窄屏或移动端。
 * @param {IMainToolbarButtonThresholds} [thresholds] - 当前生效的主栏阈值。
 * @returns {boolean}
 */
export function shouldShowToolbarQuickInput(clientWidth, isNarrowOrMobile, thresholds) {
    if (isNarrowOrMobile || clientWidth <= MAIN_TOOLBAR_NINE_BUTTONS_WIDTH) {
        return false;
    }
    if (Array.isArray(thresholds) && thresholds.length > 0) {
        const { order } = thresholds.find(({ width }) => clientWidth > width)
            || thresholds[thresholds.length - 1];
        return Array.isArray(order) && order.includes('raisehand');
    }
    return clientWidth > MAIN_TOOLBAR_NINE_BUTTONS_WIDTH;
}
/**
 * Returns the list of participant menu buttons that have that notify the api when clicked.
 *
 * @param {Object} state - The redux state.
 * @returns {Map<string, NOTIFY_CLICK_MODE>} - The list of participant menu buttons.
 */
export function getParticipantMenuButtonsWithNotifyClick(state) {
    return state['features/toolbox'].participantMenuButtonsWithNotifyClick;
}
/**
 * Returns the time, timing function and delay for elements that are position above the toolbar and need to move along
 * with the toolbar.
 *
 * @param {boolean} isToolbarVisible - Whether the toolbar is visible or not.
 * @returns {ICSSTransitionObject}
 */
export function getTransitionParamsForElementsAboveToolbox(isToolbarVisible) {
    // The transition time and delay is different to account for the time when the toolbar is about to hide/show but
    // the elements don't have to move.
    return isToolbarVisible ? {
        duration: 0.15,
        easingFunction: 'ease-in',
        delay: 0.15
    } : {
        duration: 0.24,
        easingFunction: 'ease-in',
        delay: 0
    };
}
/**
 * Converts a given object to a css transition value string.
 *
 * @param {ICSSTransitionObject} object - The object.
 * @returns {string}
 */
export function toCSSTransitionValue(object) {
    const { delay, duration, easingFunction } = object;
    return `${duration}s ${easingFunction} ${delay}s`;
}
